/**
 * VISUAL SPEC — the Studio's block doc, flattened into the Airtable columns creative reads.
 *
 * Port of the monolith's `__visualSpec` / `__vsImages` / `__vsStyling` (index.html L4995+).
 * This did not exist in the React app at all: its mirror payload carried 36 fields against the
 * monolith's ~102, and NONE of the visual spec — no Block Order, no images, no Build Spec. So
 * every image column in Airtable sat empty for any submit coming from this app. Found while
 * renaming those columns on 2026-08-20; renaming was pointless until something filled them.
 *
 * WHY THIS MATTERS FOR THE MVP: the email is still built by hand in Marketing Cloud, so the
 * Airtable row IS the build sheet. "Email Mockup URL" shows what it should look like and
 * "Block Order" says which blocks are in play; these flat fields carry every string, URL and
 * photo the division chose so a programmer never has to reverse-engineer the mockup.
 *
 * IMAGE FIELDS ARE NAMED BY ROLE, NOT NUMBERED (2026-08-20, Katy + Layne).
 * The old shape was `Image 1..6 — URL / Used In / Alt`, which meant reading "Used In" before
 * you could route anything. Now the field name says where the image goes: `Hero Image`,
 * `Featured Community Image 1..3`, `Floorplan Module Image 1..3`, `Checkerboard Image 1..3`,
 * `Map Image`, `Secondary Image 1..2`, and three `Other Block Image` slots for the nine
 * low-frequency blocks that would otherwise add eighteen mostly-empty columns.
 *
 * The numbered `Image 1..6` array was written in parallel for one release while the intake
 * mapping was repointed. RETIRED 2026-09-01 once Katy confirmed the repoint — see the note at
 * the image section below.
 */
import { FULL_REGISTRY } from "../email/blocks";
import { SINGLETON_TYPES } from "./singletonBlocks";
import { echCtasOf, echIsCtaColumnBlock, echEventOf, echImagesOf, echIsHero, echModuleOption, echSlotRole, echTextOf, type EchAware } from "./echSpecBridge";
import { echColumns, echIsAmenity, mainImageInfo } from "./echColumns";
import { heroGifSpecOf } from "./heroGif";
import { dropDuplicateCards, type AnyBlockInstance } from "../email/document";

type Row = Record<string, unknown>;
type Data = Record<string, unknown>;

const S = (v: unknown): string => (v == null ? "" : String(v));
const trim = (v: unknown): string => S(v).trim();

/** block type → its display label, straight off the registry (monolith __vsLabel) */
function label(type: string): string {
  const e = (FULL_REGISTRY as Record<string, { label?: string }>)[type];
  return (e && e.label) || type;
}

/**
 * Which data keys on each block hold an image. Ported verbatim from the monolith's
 * `__VS_IMGKEYS`; the React registry uses the same block keys, so it transfers 1:1.
 * `secondary` is the only two-image block.
 */
const IMG_KEYS: Record<string, string[]> = {
  hero: ["src"], image: ["src"], secondary: ["img", "img2"], map: ["img"], livemap: ["img"],
  livevideo: ["poster"], ei: ["img"], services: ["img"], simpler: ["img"], mortgage: ["img"],
  offer: ["img"], teaser: ["img"], nextgen: ["img"], activeadult: ["img"],
};

/**
 * A real Bynder pick vs a template default.
 *
 * Every block ships a stock placeholder in its `def()`. If the division never opened the
 * picker, that placeholder is what sits in the data — and writing it as though it were a
 * deliberate choice sends creative off to source a stock photo. A real pick always comes back
 * from /bynder-search on a lennar.bynder.com URL, so anything else is a default. Keep the URL
 * (it is what the mockup renders) but say so.
 */
const picked = (u: unknown): boolean => /(^|\/\/)([a-z0-9-]+\.)*bynder\.com\//i.test(S(u));
/* a hero GIF the Studio built is a pick even though its URL is SFMC's, not Bynder's (2026-09-16) */
const whereOf = (lab: string, u: unknown, gif?: boolean): string => lab + (picked(u) || gif ? "" : " · template default, not picked");

/** one image, with the block it belongs to and its OWN alt */
/* `block` is the CLEAN label, without the "· template default, not picked" suffix that
   `where` carries — the spillover list names its own block on every line, and that name
   has to be the block, not a note about the photo's provenance (2026-08-29). */
interface Img { url: string; where: string; alt: string; slot: string; block: string; covered?: boolean }

/**
 * Collect every image in document order, routed to its named slot.
 *
 * PER-IMAGE ALT (fixed 2026-08-20). The monolith gave every image on a block the same
 * block-level `d.alt`, so the two-image `secondary` block shipped two different photos with
 * identical alt text, and carousel slides were hardcoded to `alt: ""`. Alt is per-image here:
 * card repeaters use their own card name, `secondary` image 2 reads `alt2`, and slides read
 * `alts[i]`. Where the block genuinely has nowhere to store a second alt the value is left
 * EMPTY rather than duplicated — an empty alt is a visible gap someone can fix, a duplicated
 * one silently ships wrong text.
 *
 * `secondary` gained its `alt2` inspector input on 2026-08-21, and the block's RENDERER was
 * emitting `d.alt` on both images until the same change — so the wrong alt was going out in
 * the email, not only in this sheet. Reading a key the UI cannot write is the shape of bug to
 * watch for here: the mapper looked correct in isolation for a day.
 */
export function collectImages(doc: AnyBlockInstance[]): Img[] {
  const out: Img[] = [];
  /* per-role counters, so a second community card lands in slot 2 rather than appending */
  /* Per-role counters on ONE const object rather than five `let`s (2026-08-20).
     The callbacks below are declared inside the `for` loop and increment these, which
     is `no-loop-func` — and `CI=true` turns that warning into a failed build. A const
     binding whose PROPERTIES change is safe by the rule's own definition, and the
     numbering behaviour is unchanged: the counters still run across the whole doc. */
  const n = { community: 0, floorplan: 0, checker: 0, secondary: 0, module: 0 };

  for (const b of doc || []) {
    const d = (b.data || {}) as Data;
    const lab = label(b.type);

    /* ── AN ECH BLOCK DECLARES ITS OWN IMAGE FIELDS (2026-08-25) ────────────────────
       `IMG_KEYS` is keyed on repo types, so a document built from Layne's Marketing Cloud
       blocks (typed `sfmc_<contentId>`) matched nothing and every image column came out
       empty — measured on Katy's intake row recaDi7q6f6phIzPt, which carried a full ten-block
       ECH document and not one photo. Which keys hold an image is read off the block's OWN
       guardrail rather than guessed from a field name; see echSpecBridge. ── */
    const echImgs = echImagesOf(b as EchAware);
    if (echImgs.length) {
      const hero = echIsHero(b as EchAware);
      /* ── AN ECH BLOCK'S IMAGES GO TO ITS ROLE'S COLUMN, NOT THE SPILLOVER (2026-08-26) ──
         Katy, on an Available Now test: "the plan information is mapping to the 'other
         blocks' now" — two Featured Plan photos filed as `Other Block Image 1` and `2`.
         The repo path already routes by role (`community` → Featured Community Image,
         `map` → Map Image); the ECH path had only hero-or-spillover, so every photo off
         one of Layne's blocks landed in the three-slot overflow column. Three featured
         cards would then have consumed the whole overflow and a fourth image anywhere in
         the email would have had nowhere to go.

         Routed by the block's SFMC name via `echSlotRole`, which is the same explicit-map
         discipline as ECH_MODULE_OPTION: a block that is not named there keeps the
         spillover, so nothing is guessed into a column creative reads as authoritative. */
      const role = echSlotRole(b as EchAware);
      echImgs.forEach((im, i) => {
        let slot: string;
        /* a hero block's FIRST image is the hero; a second one on the same block is not,
           or two blocks would fight over a one-slot column */
        if (hero && i === 0) slot = "Hero Image";
        else if (role === "featured") slot = `Featured Community Image ${++n.community}`;
        else if (role === "floorplan") slot = `Floorplan Module Image ${++n.floorplan}`;
        else if (role === "checkerboard") slot = `Checkerboard Image ${++n.checker}`;
        else if (role === "map") slot = "Map Image";
        /* the two-photo block owns `Secondary Image 1..2`, same as the repo's own
           `secondary` type does below (2026-08-28) */
        else if (role === "secondary") slot = `Secondary Image ${++n.secondary}`;
        else slot = `Other Block Image ${++n.module}`;
        /* `covered` = this block already reports its images in a NAMED list of its own,
           so the generic spillover must not repeat them. Amenities is the case: the icons
           land in `Amenities Images` WITH their captions ("Pool — url"), and the spillover
           only knows the block label, so eight identical "Amenities Section — url" lines
           were shipping beside the good ones. Measured on row rectNbgzQV16szLmn. */
        out.push({ url: im.url, where: whereOf(lab, im.url, S(d[im.key + "__gif"]) === "1"), alt: im.alt, slot, block: lab,
          covered: echIsAmenity(b as EchAware) });
      });
      continue; /* an ECH block has no repo-type keys or card arrays to fall through to */
    }

    /* fixed image keys on the block itself */
    const keys = IMG_KEYS[b.type] || [];
    keys.forEach((k, ki) => {
      const u = d[k];
      if (!u || S(u).slice(0, 5) === "data:") return;
      let slot: string;
      if (b.type === "hero" || b.type === "image") slot = "Hero Image";
      else if (b.type === "map" || b.type === "livemap") slot = "Map Image";
      else if (b.type === "secondary") slot = `Secondary Image ${++n.secondary}`;
      else slot = `Other Block Image ${++n.module}`;
      /* image 2 of a two-image block reads its own alt key, never image 1's */
      const alt = ki === 0 ? trim(d.alt) : trim(d[`alt${ki + 1}`]);
      out.push({ url: S(u), where: whereOf(lab, u, S(d[k + "__gif"]) === "1"), alt, slot, block: lab });
    });

    /* carousel / multi-image arrays — alt per slide, not blank */
    if (Array.isArray(d.imgs)) {
      const alts = Array.isArray(d.alts) ? (d.alts as unknown[]) : [];
      (d.imgs as unknown[]).forEach((u, i) => {
        if (!u) return;
        out.push({
          url: S(u), where: whereOf(`${lab} · slide ${i + 1}`, u),
          alt: trim(alts[i]), slot: `Other Block Image ${++n.module}`,
          block: `${lab} · slide ${i + 1}`,
        });
      });
    }

    /* card repeaters — community / floorplan / checkerboard */
    if (Array.isArray(d.items)) {
      (d.items as Data[]).forEach((it, i) => {
        if (!it || !it.img || S(it.img).slice(0, 5) === "data:") return;
        let slot: string;
        if (b.type === "community") slot = `Featured Community Image ${++n.community}`;
        else if (b.type === "floorplan") slot = `Floorplan Module Image ${++n.floorplan}`;
        else if (b.type === "checkerboard") slot = `Checkerboard Image ${++n.checker}`;
        else slot = `Other Block Image ${++n.module}`;
        /* card repeaters do not share a field name: community/floorplan cards carry `name`,
           checkerboard rows carry `heading`. Reading only one left whole blocks alt-less. */
        out.push({
          url: S(it.img), where: whereOf(`${lab} · card ${i + 1}`, it.img),
          alt: trim(it.alt) || trim(it.name) || trim(it.heading) || trim(it.title),
          slot,
          block: `${lab} · card ${i + 1}`,
        });
      });
    }
  }
  return out;
}

/** the named slots each role owns, and how many */
const SLOT_CAPS: Record<string, number> = {
  "Hero Image": 1, "Map Image": 1, "Secondary Image": 2,
  "Featured Community Image": 3, "Floorplan Module Image": 3, "Checkerboard Image": 3,
  "Other Block Image": 3,
};

/** does this slot name fit inside its role's cap? */
function inCap(slot: string): boolean {
  const m = slot.match(/^(.*?)(?: (\d+))?$/);
  if (!m) return false;
  const role = m[1], n = m[2] ? parseInt(m[2], 10) : 1;
  const cap = SLOT_CAPS[role];
  return cap !== undefined && n <= cap;
}

/**
 * Per-block visual settings that have no column of their own — theme, inset width, overlay
 * placement, background, alignment. One "Block · setting: value" per line. All of it is visible
 * in the mockup, so this is a convenience, not a dependency; it stops a programmer eyeballing
 * a colour.
 */
export function stylingLines(doc: AnyBlockInstance[]): string {
  const L: string[] = [];
  for (const b of doc || []) {
    const d = (b.data || {}) as Data;
    const lab = label(b.type);
    if (d.__theme && d.__theme !== "white") L.push(`${lab} · theme: ${S(d.__theme)}`);
    if (d.__inset && d.__inset !== "full") L.push(`${lab} · width: ${S(d.__inset)}px inset`);
    if (d.pad && d.pad !== "none") L.push(`${lab} · width: inset`);
    if (d.bg && b.type !== "logo") L.push(`${lab} · background: ${S(d.bg)}`);
    if (d.align) L.push(`${lab} · align: ${S(d.align)}`);
    if (S(d.ovOn) === "1") {
      L.push(`${lab} · overlay: ${S(d.ovAlign) || "left"} / ${S(d.ovV) || "bottom"}, colour ${S(d.ovCol) || "#ffffff"}, size ${S(d.ovSize) || 40}px, scrim ${S(d.ovScrim) || 35}%`);
    }
  }
  return L.join("\n");
}

/** join non-empty lines — monolith __vsLines */
const lines = (...a: unknown[]): string => a.map(S).filter((x) => x.trim() !== "").join("\n");

/**
 * Block value → the EXACT Airtable select option name.
 *
 * Blocks store lowercase machine values (`filled`, `white`, `left`); the Airtable columns are
 * single-selects whose options are title-case (`Filled`, `White`). Sending the raw value fails
 * the whole row now that typecast is off — caught 2026-08-20 by posting a real spec and getting
 * `INVALID_MULTIPLE_CHOICE_OPTIONS: Insufficient permissions to create new select option
 * ""filled""`. Before typecast was turned off this would have silently minted junk options
 * instead, which is how four other fields got polluted.
 *
 * Anything unmapped returns "" rather than a guess, so an unknown value leaves the cell empty
 * instead of failing the write.
 */
const SELECT_MAP: Record<string, Record<string, string>> = {
  "CTA — Button Style": { filled: "Filled", outline: "Outline" },
  "CTA — Button Count": { "1": "One button", "2": "Two buttons", one: "One button", two: "Two buttons",
    "one button": "One button", "two buttons": "Two buttons" },
  "Logo — Background": { white: "White", navy: "Navy" },
  "Map Module": { community: "Community Map", market: "Market Map",
    "community map": "Community Map", "market map": "Market Map" },
  "Studio Mode": { blocks: "Blocks", "template edit": "Template edit", "not used": "Not used" },
};
function selectValue(field: string, raw: unknown): string {
  const v = trim(raw);
  if (!v) return "";
  const map = SELECT_MAP[field];
  if (!map) return v;                       /* not a mapped select — pass through */
  return map[v.toLowerCase()] || "";        /* unknown ⇒ empty, never a guess */
}

/**
 * Flatten the block doc into Airtable columns.
 *
 * Returns only keys that have a value, so an absent block never blanks a column — a row
 * carries the blocks that email actually uses and roughly half the columns being empty is
 * NORMAL, not missing data.
 */

/**
 * ── ONE LINE PER IMAGE, UNCAPPED (2026-08-28, Katy) ───────────────────────────
 *
 * Katy: "every image and link should map to a specific named key for its block…
 * anything without a dedicated key falls into a positional bucket, and position
 * shifts with block order, so the same slot means different things on different
 * emails. That's not something I can map against reliably." And on the shape: "A
 * capped set of numbered keys drops anything past the cap with no error. A list just
 * gets longer."
 *
 * The drop was real: `SLOT_CAPS` caps every role at three, and `inCap` DISCARDS an
 * over-cap image rather than spilling it, so plans 4 and 5 of a five-plan email
 * existed only in the legacy `Image 1..6` array — a positional bucket, which is the
 * thing she cannot map. These lists have no ceiling and pair POSITIONALLY with the
 * module list beside them: card N's photo is line N.
 *
 * "Name — URL", never "N. " — Airtable richText renders a "N. " prefix as an ordered
 * list and renumbers the lines (see the Featured Plan note below).
 */
function imageLines(items: Data[], nameOf: (it: Data, i: number) => unknown): string {
  return items
    .filter((it) => it && S(it.img))
    .map((it, i) => {
      const nm = S(nameOf(it, i));
      return nm ? `${nm} \u2014 ${S(it.img)}` : S(it.img);
    })
    .join("\n");
}

export function visualSpec(doc: AnyBlockInstance[]): Row {
  const o: Row = {};
  /* ── THE SPEC DESCRIBES THE EMAIL THAT SHIPPED, NOT THE RAW CANVAS (2026-09-22) ──────
     Justin Bradford, NEV job 68689 (asset 1034767): "NEV 68689 pulled 2 homes for the
     explore homes module, when there is only 1 home. Both homes pulled are the exact same
     … The preview only shows 1 home which is why its odd 2 pulled." The document really
     held two Featured cards resolved to the same homesite; `renderRows` has dropped the
     repeat since 08-29 (`dropDuplicateCards`, Jack: "yes spare dropped"), so the email
     carried one card — but this spec walked the raw doc, so Block Order listed Featured
     Plan twice and the Module / Featured Plan / Featured Community Image columns all
     reported the same home twice. Same filter, same order, before anything is derived:
     two renderings of one document may never disagree about how many cards there are. */
  const d = dropDuplicateCards(doc || []);
  if (!d.length) return o;

  const put = (k: string, v: unknown) => { const s = typeof v === "number" ? v : trim(v); if (s !== "" && s !== undefined) o[k] = s; };
  const first = (type: string): Data | null => {
    const b = d.find((x) => x.type === type);
    return b ? ((b.data || {}) as Data) : null;
  };

  /* ── BLOCK ORDER — the highest-value field. One label per line, top to bottom, as the
     division arranged it. Module 1/2/3 below carries only the first three modules. ── */
  put("Block Order", d.map((b) => label(b.type)).join("\n"));
  put("Studio Mode", "Blocks");

  /* ── MODULE 1 / 2 / 3 (2026-08-25, Katy on the ECH standup: "which modules are being
     selected? if it was amenities and then listing table, module one should be amenities,
     module two should be listing table — and I'm not seeing that in here either").

     They were never blank by accident: `submit.ts` reads them off `s.studioModules`, and
     NOTHING in the app has ever set that field — grep returns the declaration and the read
     and no producer. So all three columns have shipped as "" on every intake row since the
     port, which is why Katy cannot tell from the row what the email is made of.

     Derived from the DOC, in canvas order, same source as Block Order — the only thing that
     knows what the division actually assembled. `kind: "module"` is the registry's own
     content/structure split, so Logo header, Hero, Divider and Spacer never consume a slot.

     ⚠️ THESE THREE ARE singleSelect AND typecast IS OFF (deliberately, 2026-08-18). One
     value that is not already an option 422s the ENTIRE 144-field write and Katy's row
     silently never arrives — the exact failure this fix exists to end. So this maps block
     type → the EXACT option name harvested from the live field (Projects
     fldAvjnBpLpOCmLvC, 24 options, read 2026-08-25) and returns nothing for anything
     unmapped. Note "Featured Floorplan " carries a TRAILING SPACE in Airtable and
     "Checkboard" is spelled that way there — both reproduced verbatim on purpose; do not
     "fix" them here or the write starts failing. A module with no option (Trade-in, ISC,
     the Interactive family) is skipped rather than guessed: an empty slot costs Katy one
     lookup, a bad slot costs her the whole row. ── */
  const MODULE_OPTION: Record<string, string> = {
    listing: "Listing Table", community: "Featured Plan/Community", activeadult: "Active Adult",
    amenities: "Amenities", asknse: "Ask About NSE", blowout: "Blowout Sale",
    checkerboard: "Checkboard", event: "Events", ei: "Everything's Included",
    floorplan: "Featured Floorplan ", featuredtext: "Featured Text",
    flexcoupon: "ISC Flex Cash Coupon", mortgage: "Lennar Mortgage SmartBuy",
    map: "Map / Address", campaignmsg: "National Campaign Message", nextgen: "Next Gen",
    opendoor: "Opendoor", simpler: "Simpler Way to Buy", whybuy: "Why Buy Lennar",
    webinar: "FTHB Webinar", supersale: "Super Sale 2025",
  };
  /* NOT through `put` — it trims, and trimming turns Airtable's "Featured Floorplan " into a
     value that is not an option, which fails the whole row. Caught by the exhaustive test
     below, not by reading: every registered block type is driven through the builder and its
     output checked against the harvested option list. Assign the option byte-for-byte. */
  /* an ECH doc answers from its SFMC name, a repo doc from its type — see echSpecBridge */
  /* ── ONE SLOT PER MODULE TYPE, IN ORDER OF FIRST APPEARANCE (Katy, 2026-09-09) ────
     BAH's `…CourtsMeadowood_26LchSep2548Tmp_20260912…` had three Featured Plan blocks and
     one Amenities section; the row said Module 1/2/3 = "Featured Plan/Community" three times
     and Amenities never appeared in any slot. Her rule: "a module type should occupy one slot
     no matter how many times its block repeats… the repeats are already carried in Featured
     Plan/Community Module and Featured Community Image 1-5, so nothing is lost by collapsing
     them. Order should follow first appearance in Block Order." So the mapped names are
     de-duplicated here — at the block→module step, which is the one place that knows two
     block types are the same module — before they are dealt into the three slots. */
  const modules = d.map((b) => MODULE_OPTION[b.type] || echModuleOption(b as EchAware)).filter(Boolean)
    .filter((m, i, all) => all.indexOf(m) === i);
  if (modules[0]) o["Module 1"] = modules[0];
  if (modules[1]) o["Module 2"] = modules[1];
  if (modules[2]) o["Module 3"] = modules[2];

  /* ── THE WORDS ON THE CANVAS (2026-08-24) ────────────────────────────────────
     Headline, Subheadline and Copy Input were the three columns Katy reported
     empty alongside the images ("no photo, button, url or copy", 2026-08-22), and
     the images were only HALF the story. Nothing here mapped the text blocks at
     all: "Headline" came from the step-2 form input and "Copy Input" from the
     Primary Message picker — so whatever the division actually typed into the
     Studio survived only inside Build Spec (JSON), which is a debugging blob, not
     a build sheet. Marketing Cloud builds these emails by hand off this row, so
     the body copy has to be readable in a cell.

     ALL matching blocks, not `first()`: a Coming Soon build carries several copy
     blocks and taking one would silently drop the rest. Joined blank-line
     separated in canvas order, which is the order they are meant to be read in.

     These WIN over buildMirror's form-field values because visualSpec is merged
     over the base mirror — correct precedence: the canvas is what ships, the
     step-2 input is only what was asked for before the build existed. */
  const textOf = (type: string, keys: string[]) =>
    d.filter((b) => b.type === type)
      .map((b) => { const bd = (b.data || {}) as Data; for (const k of keys) { const v = trim(bd[k]); if (v) return v; } return ""; })
      .filter(Boolean)
      .join("\n\n");

  /* ECH FALLBACK (2026-08-26). `textOf` keys on the REPO type, so on a document built from
     Layne's Marketing Cloud blocks — every block typed `sfmc_<contentId>` — it matches
     nothing and these three columns ship empty on every build. `echTextOf` reads the same
     words off the block's own declared fields via the bridge, exactly as `echCtasOf` already
     does for the buttons below. Repo blocks still win: a hand-built doc keeps its existing
     answer and only falls through when there is nothing to fall back FROM. */
  const echText = (slot: "headline" | "body") =>
    d.map((b) => echTextOf(b as EchAware, slot)).filter(Boolean).join("\n\n");

  put("Headline", textOf("headline", ["text", "heading", "copy"]) || echText("headline"));
  put("Subheadline", textOf("subheadline", ["text", "copy"]));
  put(
    "Copy Input",
    textOf("body", ["text", "copy", "body"]) ||
      textOf("copy", ["text", "copy", "body"]) ||
      echText("body"),
  );

  /* ── per-block content ── */
  const logo = first("logo");
  if (logo) put("Logo Header", lines(logo.contact, logo.phone));
  if (logo && logo.bg) put("Logo — Background", selectValue("Logo — Background", logo.bg));

  const hero = first("hero") || first("image");
  if (hero) {
    /* DAM link + the URL as placed for a Bynder photo (2026-09-15, Justin) — one shape on
       both the repo-block and the ECH path; see mainImageInfo in echColumns.ts */
    put("Main Image Info", mainImageInfo(String(hero.src || ""), heroGifSpecOf(hero.src__gifsrc)));
    put("Main Image Choices", "Template Main Image");
  }

  const community = first("community");
  if (community && Array.isArray(community.items)) {
    put("Featured Plan/Community Module", (community.items as Data[])
      .filter((it) => it && (it.name || it.img))
      /* "Plan N — ", never "N. " — Airtable richText turns "N. " into a markdown ordered
         list and the indented sub-lines break it, so every card renumbered itself to 1. */
      .map((it, i) => lines(`Plan ${i + 1} — ${S(it.name)}`, `   ${S(it.desc)}`,
        `   ${[it.sqft, it.beds, it.baths].map(S).filter(Boolean).join(" | ")}`,
        it.url ? `   Button: ${S(it.link) || "View"} → ${S(it.url)}` : ""))
      .join("\n"));
    put("Featured Community Images", imageLines(community.items as Data[], (it) => it.name));
  }

  const fp = first("floorplan");
  if (fp && Array.isArray(fp.items)) {
    put("Floorplan Module", (fp.items as Data[])
      .filter((it) => it && (it.name || it.img))
      .map((it, i) => lines(`Plan ${i + 1} — ${S(it.name)}`, `   ${S(it.desc)}`,
        `   ${[it.sqft, it.beds, it.baths].map(S).filter(Boolean).join(" | ")}`,
        it.url ? `   Button: ${S(it.link) || "View"} → ${S(it.url)}` : ""))
      .join("\n"));
    put("Floorplan Module Images", imageLines(fp.items as Data[], (it) => it.name));
  }

  const cb = first("checkerboard");
  if (cb && Array.isArray(cb.items)) {
    put("Checkboard Module", (cb.items as Data[])
      .filter((it) => it && (it.heading || it.img))
      .map((it, i) => lines(`Row ${i + 1} — ${S(it.heading)}`, `   ${S(it.copy)}`,
        `   Image side: ${S(it.flip) || "left"}`, it.button ? `   Link text: ${S(it.button)}` : ""))
      .join("\n"));
    /* checkerboard rows are named by their HEADING, matching the module list above */
    put("Checkerboard Images", imageLines(cb.items as Data[], (it) => it.heading));
  }

  const listing = first("listing");
  if (listing) {
    put("Listing Table", listing.rows);
    put("Listing — View All Label", listing.viewall);
    put("Listing — View All URL", listing.viewurl);
  }

  const map = first("map") || first("livemap");
  if (map) {
    put("Map Address/Input", lines(map.collection, map.masterplan, map.address, map.cityzip,
      map.phone, map.url ? `Button: ${S(map.link) || "Get Directions"} → ${S(map.url)}` : ""));
    put("Map Module", selectValue("Map Module", map.kind || "community"));
  }

  const amen = first("amenities");
  if (amen) {
    put("Amenities", S(amen.picks).split(",").map((x) => x.trim()).filter(Boolean).join("\n"));
    /* the repo amenity block keeps its icons on `items`; the ECH path builds the same
       column from `icon<n>`/`label<n>` (echColumns). Either way the line is
       "name — url" and pairs positionally with `Amenities`. */
    if (Array.isArray(amen.items)) put("Amenities Images", imageLines(amen.items as Data[], (it) => it.name || it.label));
  }

  const isc = first("isc");
  if (isc) put("Contact Info", lines(isc.title, isc.body,
    isc.url ? `Button: ${S(isc.label) || "Contact Us"} → ${S(isc.url)}` : ""));

  /* ── EVENT DETAILS OFF AN ECH BLOCK TOO (2026-08-26) ───────────────────────────
     Katy on a Grand Opening Invite (UTH) test: "none of the event information came over
     either." `first()` matches REPO types, and an ECH block is typed `sfmc_<contentId>`,
     so this column was never written on a build assembled from Layne's blocks — the same
     miss as the image and CTA columns above. The bridge reads the invite block's own
     declared Day / Date / Time / AddressLine1 / AddressLine2. */
  const ev = first("event") || first("livecalendar");
  if (ev) put("Event Information", lines(ev.date || ev.start, ev.time || ev.end, ev.location || ev.loc));
  else {
    const echEv = d.map((b) => echEventOf(b as EchAware)).filter(Boolean).join("\n\n");
    if (echEv) put("Event Information", echEv);
  }

  const ft = first("featuredtext");
  if (ft) put("Featured Text", lines(ft.eyebrow, ft.heading, ft.copy));

  const cta = first("cta");
  if (cta) {
    put("CTA 1 — Label", cta.label || cta.label1);
    put("CTA 1 — URL", cta.url || cta.url1);
    put("CTA 2 — Label", cta.label2);
    put("CTA 2 — URL", cta.url2);
    put("CTA — Button Count", selectValue("CTA — Button Count", cta.buttons));
    put("CTA — Button Style", selectValue("CTA — Button Style", cta.style));
  }
  /* ── THE SAME TWO COLUMNS, FROM AN ECH DOCUMENT (2026-08-25) ──────────────────────
     The `cta` block above is the repo's own. Layne answers "one or two buttons" with two
     different Content Builder blocks, so an ECH build has no `cta` block at all and both
     columns came out empty — measured on Katy's row recaDi7q6f6phIzPt.
     Only fills what the repo block did not: a document is one or the other in practice, and
     if both ever appear the hand-built block is the one the canvas rendered. Button Count and
     Style are deliberately NOT inferred here — on an ECH build they are properties of WHICH
     block Layne authored, not of a field the requester set, so writing them would be reporting
     our own guess back as the division's choice. ── */
  if (!o["CTA 1 — URL"]) {
    const ctas: { label: string; url: string }[] = [];
    /* only the standalone button blocks: a Featured Plan card's button is the card's, and it
       already ships in `Featured Plan/Community Module` (FNO 68759, 2026-09-24) */
    for (const b of d) if (echIsCtaColumnBlock(b as EchAware)) for (const c of echCtasOf(b as EchAware)) ctas.push(c);
    if (ctas[0]) { put("CTA 1 — Label", ctas[0].label); put("CTA 1 — URL", ctas[0].url); }
    if (ctas[1]) { put("CTA 2 — Label", ctas[1].label); put("CTA 2 — URL", ctas[1].url); }
  }

  const footer = first("footer");
  if (footer) {
    /* ── LEGAL TEXT NO LONGER GOES TO `Disclaimer` (2026-09-01, Katy's Capture notes) ──
       This wrote the footer's legal copy into `Disclaimer`. Katy's intake column for that
       key links into the Disclaimer Builder table, whose PRIMARY field is a NUMBER, and
       Airtable matches link writes on the primary value and CREATES A NEW RECORD on no
       match — so every submission that reached here without a request number minted a
       junk row in her table holding a paragraph of boilerplate as its "number". Her note:
       "ONLY THE BARE NUMBER MATCHES. Settle this or every submission creates junk."

       The 2026-08-31 fix made the requester's number WIN over this write, which fixed the
       case where both were present and left this case — no number on the form — exactly
       as broken as before. `Disclaimer` now carries a Disclaimer Builder request number
       or nothing. The legal copy itself is not lost: it is in the email HTML and in Raw
       Payload, and it never belonged in a linked-record column. */
    const SOC: [string, string][] = [["facebook", "fb"], ["x", "x"], ["instagram", "ig"],
      ["pinterest", "pin"], ["youtube", "yt"], ["linkedin", "li"]];
    /* an EMPTY value means that icon is switched OFF and must not be rendered — an
       instruction that exists nowhere else in the payload */
    put("Footer — Social Links", SOC.map(([name, k]) => `${name}: ${S(footer[k])}`).join("\n"));
  }
  const ftf = first("ftfooter");
  if (ftf) {
    /* the ftfooter's `disclaimer` is legal copy too — same reason as above, not written */
    put("Footer — Logo URL", ftf.logo);
    put("Footer — EHO Logo URL", ftf.eho);
  }

  /* ── THE REST OF THE COLUMNS, FROM AN ECH DOCUMENT (2026-08-27) ──────────────────
     Every per-block column above keys on a REPO type, so on a document built from
     Layne's Marketing Cloud blocks they all ship empty — measured on the parity sweep's
     own row recCpENF5L2Y3spHm, where Listing Table, Amenities, Contact Info, Map
     Address/Input, Logo Header and Main Image Info were blank on a build whose canvas
     showed all of them. `echColumns` answers the same columns off the blocks' own
     declared fields, and only the ones it can genuinely answer.
     A REPO BLOCK STILL WINS: merged key by key, and only where nothing is set yet. A
     document is one or the other in practice, and if both ever appear the hand-built
     block is the one the canvas rendered. ── */
  const echCols = echColumns(d as EchAware[]);
  Object.keys(echCols).forEach((k) => { if (o[k] === undefined || o[k] === "") o[k] = echCols[k]; });

  /* ── NO "ADDITIONAL BLOCKS" COLUMN (2026-10-19, Katy) ─────────────────────────────
     The 10-14 Option B column carried every copy of a block past the first. Katy, 10-16:
     drop it — the app now holds every email to one of each module and adds no buttons
     (m2Blocks.m2OnceViolation), so each block's own column is the whole story. ── */

  put("Block Styling", stylingLines(d));

  /* ── IMAGES, into their named slots ──────────────────────────────────────────────
     ── AND THE SPILLOVER IS A LIST, NOT THREE POSITIONS (2026-08-29, Katy) ──────────
     "Anything without a dedicated key falls into a positional bucket, and position
     shifts with block order, so the same slot means different things on different
     emails. That's not something I can map against reliably."

     Both halves of that were true. `Other Block Image 1..3` was assigned in document
     order, so slot 2 was the Mortgage photo on one email and the Next Gen photo on the
     next; and `inCap` DISCARDED anything past the third — silently, mid-visualSpec, so
     a fourth unmapped image simply ceased to exist. Measured on her 2026-08-28
     amenities row: eight icons, five of them gone.

     Every line now names its own block, so position carries no meaning and nothing is
     dropped. Same shape as `Amenities Images` and `Featured Community Images`:
     "<name> — <url>", newline-delimited, never a "N. " prefix (Airtable richText would
     renumber it). The alt rides in the name when the image has one — on these blocks it
     is the only thing telling two photos apart.

     The NAMED slots are untouched: this is only the lane for blocks with no column of
     their own. `SLOT_CAPS` keeps its "Other Block Image" entry — `inCap` is no longer
     consulted for that role, and deleting it would read as if the role never existed. */
  const imgs = collectImages(d);
  o["Image Count"] = imgs.length;
  let overflow = false;
  for (const im of imgs) {
    /* ── NO "OTHER BLOCK IMAGES" COLUMN (2026-10-19, Katy) ──────────────────────────
       Its last tenant was the 240 px secondary pair, which now has its own Secondary
       Image 1/2 role (echSpecBridge ECH_SLOT_ROLE). Katy asked for the column to go;
       an image with no named slot is still counted in Image Count, never written. */
    if (im.slot.startsWith("Other Block Image")) continue;
    if (!inCap(im.slot)) { overflow = true; continue; }
    o[`${im.slot} — URL`] = im.url;
    if (im.alt) o[`${im.slot} — Alt`] = im.alt;
  }

  /* ── THE LEGACY `Image 1..6` ARRAY IS RETIRED (2026-09-01) ──────────────────────────
     It was "written in parallel for one release so the existing intake mapping keeps
     working while it is repointed". Katy's Capture notes confirm the repoint is done:
     the six numbered keys are unmapped on her side since 31 Aug, still arriving, and
     writing to nothing. She asked the app owner to stop sending them so she can strip
     them from the superset payload.

     The array was also positional against Block Order rather than semantic — on an
     email with an Amenities block, Image 5 and 6 were amenity icons, not plan photos —
     which is the whole reason the named slots and the per-block lists replaced it. The
     `overflow` it set past six images is carried by the list lane now, which has no cap.

     The SEVENTH image is the one thing this changes on our side: it used to tick
     `Extra Blocks?` here. It no longer does, because nothing is dropped any more — every
     image lands in a named slot or a named list. */

  /* ── Extra Blocks? — a COVERAGE test, not an MVP-membership test. Ticked when the flat
     fields cannot hold everything: an image past its role's cap, or a repeat of a block
     whose flat field holds one instance. Read Block Order and Build Spec (JSON) instead of
     trusting the flat fields alone. ── */
  const counts: Record<string, number> = {};
  for (const b of d) counts[b.type] = (counts[b.type] || 0) + 1;
  /* the list moved to `singletonBlocks` (2026-08-25) so the Studio can REFUSE the duplicate
     Layne asked it to refuse, instead of this only reporting it after the row is written.
     Same set, one owner — a type added for placement is a type this ticks. */
  if (SINGLETON_TYPES.some((t) => (counts[t] || 0) > 1)) overflow = true;
  o["Extra Blocks?"] = overflow;

  /* ── THE BLOB IS CAPPED (2026-08-25) ─────────────────────────────────────────────
     Measured while auditing the submit path: a filled Coming Soon with five listing
     rows sends html ~44KB and this JSON ~51KB, and `/sfmc-submit` silently refuses a
     POST around 100KB (`api.ts`: "Measured 2026-08-09: 96KB refused") — answering
     ok:true with customHtmlStored:false and no error. The retry path re-files the HTML
     only, so the blob is the half that would be lost anyway, and it is a debugging
     convenience where the html is the deliverable. Every field that Marketing Cloud
     actually builds from is a flat column above; this is dropped rather than allowed to
     push the creative over the line. `Build Spec Truncated?` says so out loud instead of
     shipping a JSON string that will not parse. */
  const specJson = JSON.stringify(d);
  const SPEC_MAX = 20000;
  if (specJson.length <= SPEC_MAX) put("Build Spec (JSON)", specJson);
  else {
    /* NO INVENTED COLUMN. The worker ALLOW-filters `mirror` to Airtable column names and
       silently drops anything else, so a new "Build Spec Truncated?" key would vanish and
       read as "there was no spec". Say it IN the existing column instead, short enough to
       cost nothing and explicit about why. */
    put("Build Spec (JSON)", JSON.stringify({ truncated: true, blocks: d.length,
      note: "omitted to keep the submit under the /sfmc-submit size limit — see the HTML" }));
  }

  return o;
}
